import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { inChunks, inChunksAll } from "@/lib/chunkedQuery";
import { fetchAllRows } from "@/lib/paginatedFetch";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useEnvironment } from "@/contexts/EnvironmentContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Bell,
  Calendar,
  Clipboard,
  AlertCircle,
  CheckCircle,
  Clock,
  Star,
  Flag,
  X,
  AlertTriangle,
  CreditCard,
  UserPlus,
  Ban,
  ClipboardList,
  FileText,
} from "lucide-react";
import { format, isPast, isToday } from "date-fns";
import { countUniqueSessions } from "@/lib/notificationUtils";
import { getExpiryInfo, expiryBadgeClass, EXPIRY_HORIZON_DAYS } from "@/lib/documentExpiry";
import { setAppBadge } from "@/lib/appBadge";

interface Reminder {
  id: string;
  subject: string;
  icon: string;
  due_date: string;
  priority: string;
  status: string;
  comments: string | null;
}

interface CoachSession {
  session_id: string;
  sessions: {
    id: string;
    date: string;
    environment: string;
  } | null;
}

interface CoachIncompleteSession {
  coach_id: string;
  coach_name: string;
  incomplete_count: number;
}

interface Incident {
  id: string;
  title: string;
  status: string;
  severity: string;
  incident_date: string;
}

interface IncidentCoachRelation {
  incident: Incident;
}

interface PendingSignup {
  id: string;
  name: string;
  email: string;
  created_at: string;
}

interface RecentCancellation {
  id: string;
  session_id: string;
  cancellation_reason: string;
  cancelled_at: string;
  session_name: string | null;
  session_date: string | null;
  venue_name: string | null;
}

interface PendingPlanner {
  id: string;
  week_start: string;
  period_type?: string | null;
  sent_at: string | null;
}

interface ExpiringDocument {
  id: string;
  type: string;
  expiry_date: string;
  coach_name: string;
}

interface NotificationsSidebarProps {
  children: React.ReactNode | ((hasNotifications: boolean) => React.ReactNode);
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  bell: Bell,
  calendar: Calendar,
  clipboard: Clipboard,
  "alert-circle": AlertCircle,
  "check-circle": CheckCircle,
  clock: Clock,
  star: Star,
  flag: Flag,
};

const priorityColors: Record<string, string> = {
  low: "bg-slate-500",
  medium: "bg-blue-500",
  high: "bg-orange-500",
  urgent: "bg-red-500",
};

const getStatusLabel = (status: string): string => {
  return status === 'investigating' ? 'Under Investigation' : status.charAt(0).toUpperCase() + status.slice(1);
};

// Cache stale time: 2 minutes
const NOTIFICATION_CACHE_STALE_MS = 2 * 60 * 1000;

export function NotificationsSidebar({ children }: NotificationsSidebarProps) {
  const { user } = useAuth();
  const { isAdmin, isCoach } = useUserRole(user?.id);
  const { showDevData } = useEnvironment();
  const [open, setOpen] = useState(false);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [incompleteSessions, setIncompleteSessions] = useState(0);
  const [adminIncompleteByCoach, setAdminIncompleteByCoach] = useState<CoachIncompleteSession[]>([]);
  const [adminIncidents, setAdminIncidents] = useState<Incident[]>([]);
  const [coachIncidents, setCoachIncidents] = useState<Incident[]>([]);
  const [unpaidByStatus, setUnpaidByStatus] = useState<{ status: string; count: number }[]>([]);
  const [pendingSignups, setPendingSignups] = useState<PendingSignup[]>([]);
  const [recentCancellations, setRecentCancellations] = useState<RecentCancellation[]>([]);
  const [pendingPlanners, setPendingPlanners] = useState<PendingPlanner[]>([]);
  const [expiringDocuments, setExpiringDocuments] = useState<ExpiringDocument[]>([]);
  const lastFetchTime = useRef<number>(0);

  const fetchIfStale = useCallback(() => {
    const now = Date.now();
    if (now - lastFetchTime.current > NOTIFICATION_CACHE_STALE_MS) {
      lastFetchTime.current = now;
      fetchNotifications();
    }
  }, [user, isAdmin, isCoach, showDevData]);

  useEffect(() => {
    if (user) {
      fetchIfStale();
    }
  }, [user, isAdmin, isCoach, showDevData]);

  // Force a fresh fetch on sign-in and clear all state on sign-out so the
  // bell badge is always accurate for the current session.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        lastFetchTime.current = 0;
        // Defer to allow user/role state to settle.
        setTimeout(() => fetchIfStale(), 50);
      } else if (event === "SIGNED_OUT") {
        lastFetchTime.current = 0;
        setReminders([]);
        setPendingApprovals(0);
        setIncompleteSessions(0);
        setAdminIncompleteByCoach([]);
        setAdminIncidents([]);
        setCoachIncidents([]);
        setUnpaidByStatus([]);
        setPendingSignups([]);
        setRecentCancellations([]);
        setPendingPlanners([]);
        setExpiringDocuments([]);
      }
    });
    return () => subscription.unsubscribe();
  }, [fetchIfStale]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      // Force fresh fetch when sidebar is opened
      lastFetchTime.current = 0;
      fetchIfStale();
    }
  };

  const fetchNotifications = async () => {
    const envValue = showDevData ? "development" : "production";

    // Fetch due reminders (due today or past and not completed)
    try {
      const { data: reminderData } = await supabase
        .from("reminders")
        .select("*")
        .eq("assigned_to", user?.id)
        .neq("status", "completed")
        .lte("due_date", format(new Date(), "yyyy-MM-dd"))
        .eq("environment", envValue)
        .order("due_date", { ascending: true });

      if (reminderData) {
        setReminders(reminderData);
      }
    } catch (error) {
      console.warn("Could not fetch reminders (table may not exist)");
    }

    // Fetch pending attendance approvals (for admins)
    if (isAdmin) {
      const pendingData = await fetchAllRows((from, to) =>
        supabase
          .from("attendances")
          .select("session_id, sessions!inner(environment, status)")
          .is("approved_at", null)
          // Only attendance that has been completed is actually awaiting approval;
          // merely marked (attended set) records are still being worked on.
          .not("completed_at", "is", null)
          .eq("sessions.environment", envValue)
          .neq("sessions.status", "Cancelled")
          .range(from, to)
      );

      // Count unique sessions instead of individual attendance records
      setPendingApprovals(countUniqueSessions(pendingData));

      // Fetch incomplete sessions by coach (for admin view)
      const today = format(new Date(), "yyyy-MM-dd");
      
      // Get all session_coaches with past sessions
      const allCoachSessions = await fetchAllRows((from, to) =>
        supabase
          .from("session_coaches")
          .select(`
            coach_id,
            session_id,
            sessions!inner(id, date, environment, status)
          `)
          .eq("sessions.environment", envValue)
          .eq("sessions.status", "Scheduled")
          .range(from, to)
      );

      if (allCoachSessions.length > 0) {
        // Filter to past sessions only
        const pastCoachSessions = allCoachSessions.filter(
          (sc: any) => sc.sessions?.date && sc.sessions.date < today
        );
        
        const pastSessionIds = [...new Set(pastCoachSessions.map((sc: any) => sc.session_id))];
        
        if (pastSessionIds.length > 0) {
          // Get ALL attendance records for past sessions to determine completeness
          const allAttendances = await inChunksAll<string, { session_id: string; completed_at: string | null }>(pastSessionIds, (c, from, to) =>
            supabase
              .from("attendances")
              .select("session_id, completed_at")
              .in("session_id", c)
              .range(from, to)
          );
          
          // Build a map: session_id -> { hasAttendees, hasIncomplete }
          const sessionAttendanceMap = new Map<string, { hasAttendees: boolean; hasIncomplete: boolean }>();
          pastSessionIds.forEach(id => sessionAttendanceMap.set(id, { hasAttendees: false, hasIncomplete: false }));
          
          allAttendances.forEach((a) => {
            const entry = sessionAttendanceMap.get(a.session_id);
            if (entry) {
              entry.hasAttendees = true;
              if (!a.completed_at) entry.hasIncomplete = true;
            }
          });
          
          // A session is incomplete if it has no attendees OR has attendees with null completed_at
          const incompleteSessionIds = new Set<string>();
          sessionAttendanceMap.forEach((info, sessionId) => {
            if (!info.hasAttendees || info.hasIncomplete) {
              incompleteSessionIds.add(sessionId);
            }
          });
          
          // Get coach names
          const coachIds = [...new Set(pastCoachSessions.map((sc: any) => sc.coach_id))];
          const { data: coachProfiles } = await inChunks<string, { id: string; name: string }>(coachIds as string[], (c) =>
            supabase.from("public_profiles").select("id, name").in("id", c));
          
          const coachNameMap = new Map(
            coachProfiles?.map((p) => [p.id, p.name]) || []
          );
          
          // Count incomplete sessions per coach
          const coachIncompleteCounts = new Map<string, number>();
          
          pastCoachSessions.forEach((sc: any) => {
            if (incompleteSessionIds.has(sc.session_id)) {
              const current = coachIncompleteCounts.get(sc.coach_id) || 0;
              coachIncompleteCounts.set(sc.coach_id, current + 1);
            }
          });
          
          // Build the result array
          const incompleteByCoach: CoachIncompleteSession[] = [];
          coachIncompleteCounts.forEach((count, coachId) => {
            incompleteByCoach.push({
              coach_id: coachId,
              coach_name: coachNameMap.get(coachId) || "Unknown Coach",
              incomplete_count: count,
            });
          });
          
          // Sort by count descending
          incompleteByCoach.sort((a, b) => b.incomplete_count - a.incomplete_count);
          setAdminIncompleteByCoach(incompleteByCoach);
        } else {
          setAdminIncompleteByCoach([]);
        }
      }
    }

    // Fetch incomplete past sessions (for coaches)
    if (isCoach) {
      const today = format(new Date(), "yyyy-MM-dd");

      // Get sessions where the coach is assigned and session is in the past
      const coachSessions = await fetchAllRows<CoachSession>((from, to) =>
        supabase
          .from("session_coaches")
          .select(`
            session_id,
            sessions!inner(id, date, environment, status)
          `)
          .eq("coach_id", user?.id)
          .eq("sessions.environment", envValue)
          .eq("sessions.status", "Scheduled")
          .range(from, to)
      );

      if (coachSessions.length > 0) {
        // Filter past sessions
        const pastSessionIds = coachSessions
          .filter((sc) => sc.sessions?.date && sc.sessions.date < today)
          .map((sc) => sc.session_id);

        if (pastSessionIds.length > 0) {
          // Get ALL attendance records for past sessions to determine completeness
          const allCoachAttendances = await inChunksAll<string, { session_id: string; completed_at: string | null }>(pastSessionIds, (c, from, to) =>
            supabase
              .from("attendances")
              .select("session_id, completed_at")
              .in("session_id", c)
              .range(from, to)
          );
          
          // Build a map: session_id -> { hasAttendees, hasIncomplete }
          const coachSessionMap = new Map<string, { hasAttendees: boolean; hasIncomplete: boolean }>();
          pastSessionIds.forEach(id => coachSessionMap.set(id, { hasAttendees: false, hasIncomplete: false }));
          
          allCoachAttendances.forEach((a) => {
            const entry = coachSessionMap.get(a.session_id);
            if (entry) {
              entry.hasAttendees = true;
              if (!a.completed_at) entry.hasIncomplete = true;
            }
          });
          
          // Count sessions that are incomplete (no attendees OR has incomplete attendees)
          let incompleteCount = 0;
          coachSessionMap.forEach((info) => {
            if (!info.hasAttendees || info.hasIncomplete) {
              incompleteCount++;
            }
          });
          
          setIncompleteSessions(incompleteCount);
        }
      }
    }

    // Fetch incidents assigned to coach (for coaches)
    if (isCoach) {
      const { data: coachIncidentsData } = await supabase
        .from("incident_coaches")
        .select(
          `
          incident:incidents!inner(id, title, status, severity, incident_date)
        `
        )
        .eq("coach_id", user?.id)
        .in("incident.status", ["open", "investigating"])
        .eq("incident.environment", envValue)
        .order("incident.incident_date", { ascending: false });

      if (coachIncidentsData) {
        const incidents = (coachIncidentsData as unknown as IncidentCoachRelation[])
          .map((ic) => ic.incident)
          .filter((i) => i !== null);
        setCoachIncidents(incidents);
      }
    }

    // Fetch planners awaiting acknowledgement for the current user
    // (coaches and admins who have planners assigned to them)
    if (user?.id) {
      const { data: plannersData } = await supabase
        .from("planners")
        .select("id, week_start, period_type, sent_at")
        .eq("coach_id", user.id)
        .eq("status", "sent")
        .order("week_start", { ascending: false })
        .limit(10);
      setPendingPlanners(plannersData ?? []);
    }

    // Fetch incidents for admins (all incidents with status 'open' or 'investigating')
    if (isAdmin) {
      const { data: adminIncidentsData } = await supabase
        .from("incidents")
        .select("id, title, status, severity, incident_date")
        .in("status", ["open", "investigating"])
        .eq("environment", envValue)
        .order("incident_date", { ascending: false });

      if (adminIncidentsData) {
        setAdminIncidents(adminIncidentsData);
      }

      // Coach documents expiring within the next 3 months (or already overdue)
      const horizonDate = new Date();
      horizonDate.setDate(horizonDate.getDate() + EXPIRY_HORIZON_DAYS);
      const { data: expiringDocs } = await supabase
        .from("coach_documents")
        .select("id, type, expiry_date, coach:profiles!coach_id(name)")
        .not("expiry_date", "is", null)
        .lte("expiry_date", format(horizonDate, "yyyy-MM-dd"))
        .order("expiry_date", { ascending: true })
        .limit(25);

      setExpiringDocuments(
        (expiringDocs ?? []).map((d: any) => ({
          id: d.id,
          type: d.type,
          expiry_date: d.expiry_date,
          coach_name: d.coach?.name || "Unknown coach",
        }))
      );

      // Fetch ALL unpaid attendance grouped by status for admins (exclude completed)
      // Paginate to avoid Supabase's default 1000-row limit
      const PAGE_SIZE = 1000;
      let allUnpaidData: Array<{ unpaid_attendance_status: string | null }> = [];
      let unpaidOffset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data: unpaidPage } = await supabase
          .from("attendances")
          .select("unpaid_attendance_status, sessions!inner(environment, status)")
          .eq("paid", false)
          .neq("unpaid_attendance_completed", true)
          .eq("sessions.environment", envValue)
          .neq("sessions.status", "Cancelled")
          .range(unpaidOffset, unpaidOffset + PAGE_SIZE - 1);

        if (unpaidPage && unpaidPage.length > 0) {
          allUnpaidData = allUnpaidData.concat(unpaidPage);
          unpaidOffset += PAGE_SIZE;
          hasMore = unpaidPage.length === PAGE_SIZE;
        } else {
          hasMore = false;
        }
      }

      if (allUnpaidData.length > 0) {
        const statusCounts = new Map<string, number>();
        allUnpaidData.forEach((record) => {
          const status = record.unpaid_attendance_status || "Not Set";
          statusCounts.set(status, (statusCounts.get(status) || 0) + 1);
        });
        const byStatus = Array.from(statusCounts.entries())
          .map(([status, count]) => ({ status, count }))
          .sort((a, b) => b.count - a.count);
        setUnpaidByStatus(byStatus);
      } else {
        setUnpaidByStatus([]);
      }

      // Fetch pending user signups (mirrors the "New user signup" push trigger)
      const { data: signupsData } = await supabase
        .from("profiles")
        .select("id, name, email, created_at")
        .eq("approved", false)
        .neq("archived", true)
        .order("created_at", { ascending: false })
        .limit(10);
      setPendingSignups(signupsData ?? []);

      // Fetch recent (last 7 days) session cancellations
      // (mirrors the "Session cancelled" push trigger)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const { data: cancellationsData } = await supabase
        .from("session_cancellations")
        .select(`
          id,
          session_id,
          cancellation_reason,
          cancelled_at,
          is_archived,
          sessions!inner(name, date, environment, venues(name))
        `)
        .eq("is_archived", false)
        .eq("sessions.environment", envValue)
        .gte("cancelled_at", sevenDaysAgo.toISOString())
        .order("cancelled_at", { ascending: false })
        .limit(10);

      if (cancellationsData) {
        setRecentCancellations(
          cancellationsData.map((c: any) => ({
            id: c.id,
            session_id: c.session_id,
            cancellation_reason: c.cancellation_reason,
            cancelled_at: c.cancelled_at,
            session_name: c.sessions?.name ?? null,
            session_date: c.sessions?.date ?? null,
            venue_name: c.sessions?.venues?.name ?? null,
          }))
        );
      } else {
        setRecentCancellations([]);
      }
    }
  };

  const handleDismissReminder = async (id: string) => {
    try {
      await supabase
        .from("reminders")
        .update({ status: "dismissed" })
        .eq("id", id);

      setReminders((prev) => prev.filter((r) => r.id !== id));
    } catch (error) {
      console.error("Error dismissing reminder:", error);
    }
  };

  const handleCompleteReminder = async (id: string) => {
    try {
      await supabase
        .from("reminders")
        .update({ status: "completed" })
        .eq("id", id);

      setReminders((prev) => prev.filter((r) => r.id !== id));
    } catch (error) {
      console.error("Error completing reminder:", error);
    }
  };

  const totalUnpaid = unpaidByStatus.reduce((sum, item) => sum + item.count, 0);

  const totalNotifications =
    reminders.length +
    (pendingApprovals > 0 ? 1 : 0) +
    (incompleteSessions > 0 ? 1 : 0) +
    (adminIncompleteByCoach.length > 0 ? 1 : 0) +
    (adminIncidents.length > 0 ? 1 : 0) +
    (coachIncidents.length > 0 ? 1 : 0) +
    (totalUnpaid > 0 ? 1 : 0) +
    (pendingSignups.length > 0 ? 1 : 0) +
    (recentCancellations.length > 0 ? 1 : 0) +
    (pendingPlanners.length > 0 ? 1 : 0) +
    (expiringDocuments.length > 0 ? 1 : 0);

  const hasNotifications = totalNotifications > 0;

  // Mirror the unread count onto the installed PWA app icon (where supported).
  useEffect(() => {
    setAppBadge(totalNotifications);
  }, [totalNotifications]);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <div className="relative">
          {typeof children === "function"
            ? children(hasNotifications)
            : children}
          {hasNotifications && typeof children !== "function" && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium animate-bounce-subtle">
              {totalNotifications > 9 ? "9+" : totalNotifications}
            </span>
          )}
        </div>
      </SheetTrigger>
      <SheetContent
        className="w-screen max-w-full sm:w-[450px] p-0 flex flex-col h-full border-l overflow-hidden"
        side="right"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="p-6 pb-0 flex-shrink-0">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notifications
              </SheetTitle>
              {totalNotifications > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    try {
                      await supabase
                        .from("reminders")
                        .update({ status: "dismissed" })
                        .eq("assigned_to", user?.id)
                        .neq("status", "completed");
                      setReminders([]);
                    } catch (error) {
                      console.error("Error clearing notifications:", error);
                    }
                  }}
                  className="text-xs"
                >
                  Clear All
                </Button>
              )}
            </div>
          </SheetHeader>
        </div>
        <ScrollArea className="flex-1 h-full w-full min-w-0 max-w-full overflow-hidden">
          <div className="w-full min-w-0 max-w-full space-y-4 overflow-hidden py-6 pl-6 pr-8">
            {/* Pending Approvals (Admin) */}
            {isAdmin && pendingApprovals > 0 && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-amber-500/20 flex-shrink-0 flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm">
                      Attendance Approvals
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {pendingApprovals} session
                      {pendingApprovals !== 1 ? "s" : ""} awaiting approval
                    </p>
                  </div>
                  <Badge variant="secondary" className="flex-shrink-0">
                    {pendingApprovals}
                  </Badge>
                </div>
              </div>
            )}

            {/* Unpaid Attendance (Admin) */}
            {isAdmin && unpaidByStatus.length > 0 && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <div className="h-8 w-8 rounded-full bg-red-500/20 flex-shrink-0 flex items-center justify-center">
                    <CreditCard className="h-4 w-4 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm mb-2">Unpaid Attendance</h4>
                    <div className="space-y-1">
                      {unpaidByStatus.map((item) => (
                        <div key={item.status} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground truncate">{item.status}</span>
                          <Badge variant="secondary" className="flex-shrink-0 ml-2">
                            {item.count}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Badge variant="secondary" className="flex-shrink-0">
                    {totalUnpaid}
                  </Badge>
                </div>
              </div>
            )}

            {/* Incomplete Sessions (Coach) */}
            {isCoach && incompleteSessions > 0 && (
              <a
                href="/attendance"
                onClick={() => {
                  try { localStorage.setItem("attendance-tab", "mine"); } catch { /* ignore */ }
                  setOpen(false);
                }}
                className="block p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg hover:bg-orange-500/15 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-orange-500/20 flex-shrink-0 flex items-center justify-center">
                    <Clipboard className="h-4 w-4 text-orange-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm">
                      Incomplete Attendance
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {incompleteSessions} past session
                      {incompleteSessions !== 1 ? "s" : ""} need completion — tap to view
                    </p>
                  </div>
                  <Badge variant="secondary" className="flex-shrink-0">
                    {incompleteSessions}
                  </Badge>
                </div>
              </a>
            )}

            {/* Incomplete Sessions by Coach (Admin) */}
            {isAdmin && adminIncompleteByCoach.length > 0 && (
              <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <div className="h-8 w-8 rounded-full bg-orange-500/20 flex-shrink-0 flex items-center justify-center">
                    <Clipboard className="h-4 w-4 text-orange-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm mb-2">
                      Incomplete Attendance by Coach
                    </h4>
                    <div className="space-y-1">
                      {adminIncompleteByCoach.map((coach) => (
                        <div key={coach.coach_id} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground truncate">{coach.coach_name}</span>
                          <Badge variant="secondary" className="flex-shrink-0 ml-2">
                            {coach.incomplete_count}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Expiring Coach Documents (Admin) */}
            {isAdmin && expiringDocuments.length > 0 && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <div className="h-8 w-8 rounded-full bg-amber-500/20 flex-shrink-0 flex items-center justify-center">
                    <FileText className="h-4 w-4 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm mb-2">Documents Expiring</h4>
                    <div className="space-y-1">
                      {expiringDocuments.map((doc) => {
                        const info = getExpiryInfo(doc.expiry_date);
                        return (
                          <div key={doc.id} className="flex items-center justify-between gap-2 text-xs">
                            <span className="text-muted-foreground truncate">
                              {doc.coach_name} · {doc.type}
                            </span>
                            <Badge
                              variant="outline"
                              className={`flex-shrink-0 ${expiryBadgeClass(info?.tone ?? "ok")}`}
                            >
                              {info?.label ?? ""}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <Badge variant="secondary" className="flex-shrink-0">
                    {expiringDocuments.length}
                  </Badge>
                </div>
              </div>
            )}

            {/* Open Incidents (Admin) */}
            {isAdmin && adminIncidents.length > 0 && (
              <div className="w-full min-w-0 max-w-full overflow-hidden p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex w-full min-w-0 items-start gap-2 overflow-hidden">
                  <div className="h-8 w-8 rounded-full bg-red-500/20 flex-shrink-0 flex items-center justify-center">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm mb-2">
                      Open Incidents
                    </h4>
                    <div className="min-w-0 max-w-full space-y-2 overflow-hidden">
                      {adminIncidents.slice(0, 3).map((incident) => (
                        <div key={incident.id} className="min-w-0 max-w-full overflow-hidden text-xs">
                          <div className="flex min-w-0 max-w-full items-start gap-2 overflow-hidden">
                            <span className="block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground">
                              {incident.title}
                            </span>
                            <Badge 
                              variant="secondary" 
                              className="flex-shrink-0 text-xs capitalize"
                            >
                              {getStatusLabel(incident.status)}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {format(new Date(incident.incident_date), "MMM d, yyyy")}
                          </div>
                        </div>
                      ))}
                      {adminIncidents.length > 3 && (
                        <p className="text-xs text-muted-foreground italic">
                          +{adminIncidents.length - 3} more incidents
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pending User Signups (Admin) */}
            {isAdmin && pendingSignups.length > 0 && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <div className="h-8 w-8 rounded-full bg-blue-500/20 flex-shrink-0 flex items-center justify-center">
                    <UserPlus className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm mb-2">
                      Pending User Signups
                    </h4>
                    <div className="space-y-2">
                      {pendingSignups.slice(0, 3).map((signup) => (
                        <div key={signup.id} className="text-xs">
                          <div className="text-muted-foreground truncate">
                            {signup.name}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5 truncate">
                            {signup.email}
                          </div>
                        </div>
                      ))}
                      {pendingSignups.length > 3 && (
                        <p className="text-xs text-muted-foreground italic">
                          +{pendingSignups.length - 3} more awaiting approval
                        </p>
                      )}
                    </div>
                  </div>
                  <Badge variant="secondary" className="flex-shrink-0">
                    {pendingSignups.length}
                  </Badge>
                </div>
              </div>
            )}

            {/* Recent Session Cancellations (Admin) */}
            {isAdmin && recentCancellations.length > 0 && (
              <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <div className="h-8 w-8 rounded-full bg-rose-500/20 flex-shrink-0 flex items-center justify-center">
                    <Ban className="h-4 w-4 text-rose-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm mb-2">
                      Recent Cancellations
                    </h4>
                    <div className="space-y-2">
                      {recentCancellations.slice(0, 3).map((c) => (
                        <div key={c.id} className="text-xs">
                          <div className="text-muted-foreground truncate">
                            {c.venue_name || c.session_name || "Session"}
                            {c.session_date &&
                              ` — ${format(new Date(c.session_date), "MMM d")}`}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5 truncate">
                            {c.cancellation_reason}
                          </div>
                        </div>
                      ))}
                      {recentCancellations.length > 3 && (
                        <p className="text-xs text-muted-foreground italic">
                          +{recentCancellations.length - 3} more in last 7 days
                        </p>
                      )}
                    </div>
                  </div>
                  <Badge variant="secondary" className="flex-shrink-0">
                    {recentCancellations.length}
                  </Badge>
                </div>
              </div>
            )}

            {/* Assigned Incidents (Coach) */}
            {isCoach && coachIncidents.length > 0 && (
              <div className="w-full min-w-0 max-w-full overflow-hidden p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex w-full min-w-0 items-start gap-2 overflow-hidden">
                  <div className="h-8 w-8 rounded-full bg-red-500/20 flex-shrink-0 flex items-center justify-center">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm mb-2">
                      Assigned Incidents
                    </h4>
                    <div className="min-w-0 max-w-full space-y-2 overflow-hidden">
                      {coachIncidents.slice(0, 3).map((incident) => (
                        <div key={incident.id} className="min-w-0 max-w-full overflow-hidden text-xs">
                          <div className="flex min-w-0 max-w-full items-start gap-2 overflow-hidden">
                            <span className="block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground">
                              {incident.title}
                            </span>
                            <Badge 
                              variant="secondary" 
                              className="flex-shrink-0 text-xs capitalize"
                            >
                              {getStatusLabel(incident.status)}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {format(new Date(incident.incident_date), "MMM d, yyyy")}
                          </div>
                        </div>
                      ))}
                      {coachIncidents.length > 3 && (
                        <p className="text-xs text-muted-foreground italic">
                          +{coachIncidents.length - 3} more incidents
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pending Planners (assigned to current user) */}
            {pendingPlanners.length > 0 && (
              <div className="p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <div className="h-8 w-8 rounded-full bg-indigo-500/20 flex-shrink-0 flex items-center justify-center">
                    <ClipboardList className="h-4 w-4 text-indigo-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm mb-2">
                      Planners to Acknowledge
                    </h4>
                    <div className="space-y-2">
                      {pendingPlanners.slice(0, 3).map((p) => (
                        <a
                          key={p.id}
                          href={`/planners/${p.id}`}
                          onClick={() => setOpen(false)}
                          className="block text-xs hover:underline"
                        >
                          <div className="text-muted-foreground truncate">
                            {p.period_type === "monthly"
                              ? format(new Date(p.week_start), "MMMM yyyy")
                              : `Week of ${format(new Date(p.week_start), "MMM d, yyyy")}`}
                          </div>
                          {p.sent_at && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              Sent {format(new Date(p.sent_at), "MMM d")}
                            </div>
                          )}
                        </a>
                      ))}
                      {pendingPlanners.length > 3 && (
                        <p className="text-xs text-muted-foreground italic">
                          +{pendingPlanners.length - 3} more awaiting
                        </p>
                      )}
                    </div>
                  </div>
                  <Badge variant="secondary" className="flex-shrink-0">
                    {pendingPlanners.length}
                  </Badge>
                </div>
              </div>
            )}

            {(isAdmin && pendingApprovals > 0) ||
            (isCoach && incompleteSessions > 0) ||
            (isAdmin && adminIncompleteByCoach.length > 0) ||
            (isAdmin && adminIncidents.length > 0) ||
            (isCoach && coachIncidents.length > 0) ||
            pendingPlanners.length > 0 ? (
              <Separator />
            ) : null}

            {/* Reminders */}
            {reminders.length > 0 && (
              <>
                <h3 className="text-sm font-medium text-muted-foreground px-1">
                  Due Reminders
                </h3>
                {reminders.map((reminder) => {
                  const IconComponent = iconMap[reminder.icon] || Bell;
                  const isOverdue =
                    isPast(new Date(reminder.due_date)) &&
                    !isToday(new Date(reminder.due_date));

                  return (
                    <div
                      key={reminder.id}
                      className={`p-3 border rounded-lg ${
                        isOverdue
                          ? "bg-destructive/10 border-destructive/30"
                          : "bg-card"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div
                          className={`h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                            priorityColors[reminder.priority]
                          }/20`}
                        >
                          <IconComponent
                            className={`h-4 w-4 ${
                              isOverdue ? "text-destructive" : "text-foreground"
                            }`}
                          />
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-sm truncate">
                              {reminder.subject}
                            </h4>
                            <Badge
                              variant="outline"
                              className={`text-xs flex-shrink-0 ${
                                priorityColors[reminder.priority]
                              } text-white border-0`}
                            >
                              {reminder.priority}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Due:{" "}
                            {format(new Date(reminder.due_date), "MMM d, yyyy")}
                            {isOverdue && " (Overdue)"}
                          </p>
                          {reminder.comments && (
                            <p className="text-xs mt-1 text-muted-foreground truncate">
                              {reminder.comments}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleCompleteReminder(reminder.id)}
                            title="Mark complete"
                          >
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleDismissReminder(reminder.id)}
                            title="Dismiss"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {totalNotifications === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No notifications</p>
                <p className="text-sm">You're all caught up!</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
